// Removed toggle icons; email is always shown and phone is optional
import { useEffect, useState } from "react";
import { YStack } from "tamagui";
import { SignupData } from "../../api/auth.types";
import { useSignupAuth } from "../../hooks/SignupAuthContext";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import { GoogleLogo } from "../shared/icons/GoogleLogo";
import PhoneInput from "../shared/phone-input";
import TamaguiInput from "../shared/tamagui-input";
import Typography from "../shared/typography";
import { formatPhoneNumberToE164 } from "../utls/utils";

interface SignupFormProps {
  onToggleView: () => void;
  presetValues?: SignupData;
}

function SignupForm({ onToggleView, presetValues }: SignupFormProps) {
  const { signup, isLoading, signupError, signUpWithGoogle } = useSignupAuth();

  const [email, setEmail] = useState(presetValues?.email || "");
  const [phone, setPhone] = useState(presetValues?.phone || "");
  const [showPhone, setShowPhone] = useState<boolean>(!!presetValues?.phone);
  const [password, setPassword] = useState("");
  const [existingUserId] = useState<string | null>(
    presetValues?.existingUserId || null
  );
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState(presetValues?.firstName || "");
  const [lastName, setLastName] = useState(presetValues?.lastName || "");
  const [passwordMatchError, setPasswordMatchError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordMatchError("Passwords do not match");
      return;
    }
    setPasswordMatchError("");

    await signup({
      firstName,
      lastName,
      email,
      password,
      phone: phone || undefined,
      existingUserId: existingUserId || undefined,
      slug: presetValues?.slug || undefined,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const firstNameParam = params.get("firstName");
    const lastNameParam = params.get("lastName");
    const emailParam = params.get("email");
    const phoneParam = params.get("phone");

    // Independently set values so both can be provided
    if (firstNameParam !== null) setFirstName(firstNameParam);
    if (lastNameParam !== null) setLastName(lastNameParam);
    if (emailParam) setEmail(emailParam);
    if (phoneParam) setPhone(formatPhoneNumberToE164(phoneParam));

    // Show phone input if a phone is present
    if (phoneParam || presetValues?.phone) {
      setShowPhone(true);
    }
  }, [window.location.search, presetValues?.email, presetValues?.phone]);

  const error = signupError || passwordMatchError;

  return (
    <Card
      className="w-full max-w-md mx-auto"
      variant="cream"
      elevation={2}
      hoverEffect
    >
      <Card.Header className="text-center" variant="default">
        <Typography variant="h3" className="text-primary">
          Create Account
        </Typography>
      </Card.Header>

      <Card.Body>
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
            <Typography variant="body2">{error}</Typography>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <YStack gap="$3">
            <TamaguiInput
              id="firstName"
              label="First Name"
              value={firstName}
              onChangeText={setFirstName}
              placeholder="Enter your full name"
              fullWidth
            />
            <TamaguiInput
              id="lastName"
              label="Last Name"
              value={lastName}
              onChangeText={setLastName}
              placeholder="Enter your full name"
              fullWidth
            />

            <TamaguiInput
              id="email"
              label="Email"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
              fullWidth
              inputType="email"
            />

            {showPhone && (
              <PhoneInput
                id="phone"
                label="Phone (optional)"
                value={phone}
                onChangeText={setPhone}
                placeholder="(310) 456-7890"
                fullWidth
              />
            )}

            <div>
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                onClick={() => setShowPhone((v) => !v)}
              >
                {showPhone ? "Remove phone" : "Add phone (optional)"}
              </button>
            </div>

            <TamaguiInput
              id="password"
              label="Password"
              inputType="password"
              value={password}
              onChangeText={setPassword}
              placeholder="Create a password"
              fullWidth
            />

            <TamaguiInput
              id="confirmPassword"
              label="Confirm Password"
              inputType="password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Confirm your password"
              fullWidth
              error={passwordMatchError ? "Passwords do not match" : ""}
            />

            <div className="pt-2">
              <SubmitButton
                onClick={() => {}}
                onSubmit={handleSubmit}
                isDisabled={
                  !firstName ||
                  !lastName ||
                  !email ||
                  !password ||
                  !confirmPassword ||
                  isLoading
                }
                iconOnly={false}
                size="md"
                className="w-full"
              >
                Create Account
              </SubmitButton>
            </div>
          </YStack>
        </form>
        {/* The google oauth flow does not have a way to pass in the existing user id, so we need to hide the google sign up button if the existing user id is provided. In the future we can add a way to pass in the existing user id after the oauth flow. */}
        {!existingUserId && (
          <div className="mt-4 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-primary"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <Typography
                variant="caption"
                className="px-2 bg-cream text-primary"
              >
                Or continue with
              </Typography>
            </div>
          </div>
        )}

        {!existingUserId && (
          <div className="mt-4">
            <button
              type="button"
              onClick={signUpWithGoogle}
              className="w-full flex justify-center items-center py-2 px-4 border border-primary rounded-md shadow-sm bg-white text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <GoogleLogo />
              Sign up with Google
            </button>
          </div>
        )}
      </Card.Body>

      <Card.Footer divider className="text-center" variant="default">
        <Typography variant="body2" className="text-terracotta">
          Already have an account?{" "}
          <button
            onClick={onToggleView}
            className="text-primary hover:text-primary-dark font-medium"
          >
            Log In
          </button>
        </Typography>
      </Card.Footer>
    </Card>
  );
}

export default SignupForm;
